/**
 * The one pagination block for the admin panel.
 *
 * Reference DOM (user, 2026-09-20 — every paginated report ships this):
 *
 *   div.row.pt-3 > div.col
 *     > div.dataTables_paginate.paging_simple_numbers.float-right
 *       > ul.pagination.pagination-rounded.mb-0            ← outer, styling only
 *         > ul[role=menubar][aria-label=Pagination].pagination.dataTables_paginate
 *             .paging_simple_numbers.my-0.b-pagination.justify-content-end
 *           li.page-item        «  ‹   (button, or span.page-link when disabled)
 *           li.page-item[.active]  1 2 3 …   (button[role=menuitemradio])
 *           li.page-item        ›  »
 *
 * The nested `ul` is the reference's own markup (bootstrap-vue's b-pagination
 * rendered inside the theme's `ul.pagination`), not a mistake — keep it.
 *
 * Page numbers follow bootstrap-vue exactly (pageWindow / xsHidden below):
 * a window around the current page where an ellipsis takes a number's slot,
 * and only 3 numbers on a phone. Before this, each report drew its own copy of this block and
 * most of them showed ONLY the active page, so a 6-page report gave no way to
 * jump to page 3.
 */

// Same default as bootstrap-vue's b-pagination.
const DEFAULT_LIMIT = 5;

// bootstrap-vue never shows ellipses at or below this limit.
const ELLIPSIS_THRESHOLD = 3;

/**
 * bootstrap-vue b-pagination's own window (paginationParams, no first/last
 * number, ellipsis on). An ellipsis takes a number's slot, so with limit 5:
 * start "1 2 3 4 …", middle "… 4 5 6 …", end "… 7 8 9 10". The old window
 * always drew 5 numbers plus the ellipses (user 2026-10-19: desktop shows 4).
 */
function pageWindow(currentPage, totalPages, limit) {
  let showFirstDots = false;
  let showLastDots = false;
  let count = limit;
  let start = 1;
  if (totalPages <= limit) {
    count = totalPages;
  } else if (currentPage < limit - 1 && limit > ELLIPSIS_THRESHOLD) {
    showLastDots = true;
    count = limit - 1;
  } else if (totalPages - currentPage + 2 < limit && limit > ELLIPSIS_THRESHOLD) {
    showFirstDots = true;
    count = limit - 1;
    start = totalPages - count + 1;
  } else {
    if (limit > ELLIPSIS_THRESHOLD) {
      count = limit - 2;
      showFirstDots = true;
      showLastDots = true;
    }
    start = currentPage - Math.floor(count / 2);
  }
  if (start < 1) {
    start = 1;
    showFirstDots = false;
  } else if (start > totalPages - count) {
    start = totalPages - count + 1;
    showLastDots = false;
  }
  count = Math.min(count, totalPages - start + 1);
  const pages = Array.from({ length: count }, (_, i) => start + i);
  return { pages, showFirstDots, showLastDots };
}

/**
 * bootstrap-vue pageList: on xs screens (bv-d-xs-down-none, < 576px) only 3
 * numbers stay — the current page and one each side (the first / last 3 at
 * the ends), so the row never wraps on a phone.
 */
function xsHidden(pages, currentPage) {
  const hidden = new Set();
  if (pages.length <= 3) return hidden;
  const idx = currentPage - pages[0];
  pages.forEach((p, i) => {
    const hide = idx === 0 ? i >= 3
      : idx === pages.length - 1 ? i < pages.length - 3
      : i < idx - 1 || i > idx + 1;
    if (hide) hidden.add(p);
  });
  return hidden;
}

/** « ‹ › » — a button while it can be used, a disabled span once it cannot. */
function ArrowItem({ label, aria, disabled, page, onPageChange }) {
  return (
    <li role="presentation" aria-hidden={disabled ? 'true' : undefined} className={`page-item${disabled ? ' disabled' : ''}`}>
      {disabled ? (
        <span role="menuitem" aria-label={aria} aria-disabled="true" className="page-link">{label}</span>
      ) : (
        <button
          role="menuitem"
          type="button"
          tabIndex="-1"
          aria-label={aria}
          className="page-link"
          onClick={() => onPageChange(page)}
        >
          {label}
        </button>
      )}
    </li>
  );
}

function Pagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  limit = DEFAULT_LIMIT,
  // Reports wrap this in their own row/col; pass wrap={false} when the caller
  // already provides one.
  wrap = true,
}) {
  const total = Math.max(1, Number(totalPages) || 1);
  const page = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const go = (p) => {
    const next = Math.min(Math.max(1, p), total);
    if (next !== page && typeof onPageChange === 'function') onPageChange(next);
  };

  const { pages, showFirstDots, showLastDots } = pageWindow(page, total, limit);
  const xsHide = xsHidden(pages, page);

  const ellipsis = (key) => (
    <li key={key} role="separator" className="page-item disabled bv-d-xs-down-none">
      <span className="page-link">…</span>
    </li>
  );

  const block = (
    <div className="dataTables_paginate paging_simple_numbers float-right">
      <ul className="pagination pagination-rounded mb-0">
        <ul
          role="menubar"
          aria-disabled="false"
          aria-label="Pagination"
          className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
        >
          <ArrowItem label="&laquo;" aria="Go to first page" disabled={page === 1} page={1} onPageChange={go} />
          <ArrowItem label="&lsaquo;" aria="Go to previous page" disabled={page === 1} page={page - 1} onPageChange={go} />
          {showFirstDots && ellipsis('lead')}
          {pages.map((p) => {
            const active = p === page;
            return (
              <li key={p} role="presentation" className={`page-item${active ? ' active' : ''}${xsHide.has(p) ? ' bv-d-xs-down-none' : ''}`}>
                <button
                  role="menuitemradio"
                  type="button"
                  aria-label={`Go to page ${p}`}
                  aria-checked={active ? 'true' : 'false'}
                  aria-posinset={p}
                  aria-setsize={total}
                  tabIndex={active ? '0' : '-1'}
                  className="page-link"
                  onClick={() => go(p)}
                >
                  {p}
                </button>
              </li>
            );
          })}
          {showLastDots && ellipsis('trail')}
          <ArrowItem label="&rsaquo;" aria="Go to next page" disabled={page >= total} page={page + 1} onPageChange={go} />
          <ArrowItem label="&raquo;" aria="Go to last page" disabled={page >= total} page={total} onPageChange={go} />
        </ul>
      </ul>
    </div>
  );

  if (!wrap) return block;

  return (
    <div className="row pt-3">
      <div className="col">
        {block}
      </div>
    </div>
  );
}

export default Pagination;
